import type { Theme } from './types';

// Generates the scoped stylesheet injected inside the widget's Shadow Root.
// Host-site CSS cannot reach in; widget CSS cannot leak out.
export function buildStyles(theme: Theme | null): string {
  const t: Required<Theme> = {
    primary: theme?.primary ?? '#0a2540',
    onPrimary: theme?.onPrimary ?? '#ffffff',
    accent: theme?.accent ?? '#27c2a0',
    radius: theme?.radius ?? '12px',
    font:
      theme?.font ??
      'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  };

  return `
    :host {
      --vc-primary: ${t.primary};
      --vc-on-primary: ${t.onPrimary};
      --vc-accent: ${t.accent};
      --vc-radius: ${t.radius};
      --vc-font: ${t.font};
      font-family: var(--vc-font);
      color: #16253c;
      /* Position is set inline on the host element from index.ts — inline
         styles win over :host rules, so we deliberately do not set position
         properties here. */
    }
    * { box-sizing: border-box; }
    button { font: inherit; }

    .vc-launcher {
      background: var(--vc-primary);
      color: var(--vc-on-primary);
      border: none;
      border-radius: 999px;
      padding: 12px 18px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 8px 24px rgba(10, 37, 64, 0.25);
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }
    .vc-launcher:hover { filter: brightness(1.05); }

    .vc-panel {
      width: 360px;
      max-width: calc(100vw - 48px);
      height: 560px;
      max-height: calc(100vh - 120px);
      background: #fff;
      border-radius: var(--vc-radius);
      box-shadow: 0 16px 40px rgba(10, 37, 64, 0.25);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .vc-header {
      background: var(--vc-primary);
      color: var(--vc-on-primary);
      padding: 14px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .vc-header-title { font-weight: 600; font-size: 15px; }
    .vc-close {
      background: transparent;
      color: inherit;
      border: none;
      font-size: 20px;
      cursor: pointer;
      line-height: 1;
      padding: 0 4px;
    }

    .vc-log {
      flex: 1;
      overflow-y: auto;
      padding: 14px;
      background: #f6f8fb;
    }
    .vc-msg { margin-bottom: 10px; display: flex; }
    .vc-msg.vc-user { justify-content: flex-end; }
    .vc-bubble {
      max-width: 80%;
      padding: 10px 12px;
      border-radius: 14px;
      font-size: 14px;
      line-height: 1.4;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
    .vc-user .vc-bubble {
      background: var(--vc-primary);
      color: var(--vc-on-primary);
      border-bottom-right-radius: 4px;
    }
    .vc-assistant .vc-bubble {
      background: #fff;
      border: 1px solid #e4e8ef;
      border-bottom-left-radius: 4px;
    }
    .vc-typing .vc-bubble::after {
      content: '…';
      animation: vc-blink 1s steps(3, end) infinite;
    }
    @keyframes vc-blink {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 1; }
    }

    .vc-quick {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 8px 14px 0;
      background: #f6f8fb;
    }
    .vc-chip {
      background: #fff;
      border: 1px solid var(--vc-accent);
      color: var(--vc-primary);
      border-radius: 999px;
      padding: 6px 12px;
      font-size: 13px;
      cursor: pointer;
    }
    .vc-chip:hover { background: var(--vc-accent); color: var(--vc-on-primary); }

    .vc-input-row {
      display: flex;
      padding: 10px;
      gap: 8px;
      border-top: 1px solid #e4e8ef;
      background: #fff;
    }
    .vc-input {
      flex: 1;
      border: 1px solid #d6dce7;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 14px;
      outline: none;
      font-family: inherit;
      color: inherit;
    }
    .vc-input:focus { border-color: var(--vc-primary); }
    .vc-send {
      background: var(--vc-primary);
      color: var(--vc-on-primary);
      border: none;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .vc-send:disabled { opacity: 0.5; cursor: not-allowed; }

    .vc-lead {
      padding: 12px 14px;
      background: #fff;
      border-top: 1px solid #e4e8ef;
    }
    .vc-lead-copy { font-size: 13px; margin: 0 0 8px; color: #48586e; }
    .vc-lead-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .vc-lead input, .vc-lead textarea {
      border: 1px solid #d6dce7;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      width: 100%;
      font-family: inherit;
    }
    .vc-lead textarea { grid-column: span 2; min-height: 60px; resize: vertical; }
    .vc-lead-row { grid-column: span 2; display: flex; gap: 8px; justify-content: flex-end; }
    .vc-lead-submit {
      background: var(--vc-primary);
      color: var(--vc-on-primary);
      border: none;
      border-radius: 8px;
      padding: 8px 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .vc-lead-cancel {
      background: transparent;
      border: 1px solid #d6dce7;
      border-radius: 8px;
      padding: 8px 14px;
      cursor: pointer;
    }
    .vc-error {
      color: #b02a37;
      font-size: 13px;
      padding: 6px 14px;
      background: #fef2f3;
      border-top: 1px solid #f5d5d9;
    }
    @media (prefers-reduced-motion: reduce) {
      .vc-typing .vc-bubble::after { animation: none; }
    }
  `;
}
